import { Controller } from 'stimulus';
import Rails from '@rails/ujs';
import { debounce } from 'lodash';

export default class extends Controller {
  static values = {
    url: String,
    hasNext: Boolean,
    selectableCheckboxName: String,
    selectableCommunicationStrategy: String,
    selectableCounterOne: String,
    selectableCounterOther: String,
    selectableSelectedElements: { type: Array, default: [] },
    scrollInside: Boolean,
    container: String,
    showRecordCount: Boolean,
    recordCounterOne: String,
    recordCounterOther: String,
    searchCounterOne: String,
    searchCounterOther: String,
    requestMethod: String,
    selectionMode: String,
    recordCount: Number,
    allowSelectAll: Boolean,
    csvUrl: String,
    filters: { type: Object, default: {} }
  };

  static targets = [
    'searchInput',
    'table',
    'infiniteScrollTrigger',
    'searchEmptyState',
    'selectableCounter',
    'selectableCounterTooltip',
    'toggleableDropdown',
    'toggleableCheckbox',
    'tableHint',
    'recordCounter',
    'countersContainer',
    'startDateInput',
    'endDateInput',
    'csvDownloadLink'
  ];

  connect() {
    const savedState = localStorage.getItem(this.containerValue);

    if (savedState) {
      this.restoreState(savedState);
    } else {
      this.state = {
        search: '',
        sorting: {
          column: null,
          originalDirection: null,
          direction: 'asc'
        },
        page: 1,
        hasNext: this.hasNextValue,
        selectedItems: new Map(),
        // Filters and dates picked when the page was rendered carry over to the
        // next page, sort or search.
        filters: new Map(Object.entries(this.filtersValue)),
        startDate: this.hasStartDateInputTarget ? this.startDateInputTarget.value : undefined,
        endDate: this.hasEndDateInputTarget ? this.endDateInputTarget.value : undefined,
        selectionMode: this.selectionModeValue || 'none',
        totalCount: this.recordCountValue
      };
    }

    this.searchOnInput = debounce(this.handleSearch.bind(this), 300);

    this.setupInfiniteScroll();
    this._updatedSelectedItemsState();
    this._updateSelectedColumnsState();
    this.setHeaderContainerProperties();

    document.addEventListener('promote:data-table:clear-selection', this.clearSelection.bind(this));

    this.boundToggleSelectAll = this.toggleSelectAll.bind(this);
    this.boundHandleRowDeletion = this.handleRowDeletion.bind(this);

    document.addEventListener('promote:checkbox-select-all', this.boundToggleSelectAll);
    document.addEventListener('promote:checkbox-deselect-all', this.boundToggleSelectAll);
    document.addEventListener('promote:data-table:row-deleted', this.boundHandleRowDeletion);
  }

  initializeObserver(callback) {
    const root = this.scrollInsideValue ?
      this.tableTarget.querySelector('.rows-section') :
      null;

    return new IntersectionObserver(
      callback.bind(this),
      {
        root: root,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );
  }

  handleNextPageLoad(entries) {
    entries.forEach((entry) => {
      if (entry.intersectionRatio !== 1) {
        return;
      }

      if (!this.state.hasNext) {
        this.infiniteScrollTriggerTarget.classList.add("hidden");

        return;
      }

      this.state.page += 1;
      this.fetchData();
    });
  }

  disconnect() {
    if (this.nextObserver) {
      this.nextObserver.disconnect();
    }

    document.removeEventListener('promote:data-table:clear-selection', this.clearSelection.bind(this));
    document.removeEventListener('promote:checkbox-select-all', this.boundToggleSelectAll);
    document.removeEventListener('promote:checkbox-deselect-all', this.boundToggleSelectAll);
    document.removeEventListener('promote:data-table:row-deleted', this.boundHandleRowDeletion);
  }

  filter(event) {
    const selectedElement = event.currentTarget;
    const filterName = selectedElement.closest('.js-data-table-filter').dataset.filterName;
    const filterValue = selectedElement.dataset.value;

    if (filterValue) {
      this.state.filters.set(filterName, filterValue);
    } else {
      this.state.filters.delete(filterName);
    }

    this.state.page = 1;

    this.fetchData();
  }

  filtered() {
    return this.state.filters.size > 0 || Boolean(this.state.startDate || this.state.endDate);
  }

  filterByDate(event) {
    const input = event.currentTarget;
    const filterName = input.dataset.filterName;

    if (filterName === 'start_date') {
      this.state.startDate = input.value;
    } else if (filterName === 'end_date') {
      this.state.endDate = input.value;
    }

    this.state.page = 1;
    this.fetchData();
  }

  sort(event) {
    event.preventDefault();

    if (this.ajaxRequest) this.ajaxRequest.abort();

    const { sortableIdentifier: column, sortableDirection: direction } = event.target.dataset;

    this.state.sorting = { column, originalDirection: direction, direction };
    this.state.page = 1;

    this.fetchData();
  }

  toggleSelectAll(event) {
    this._updateSelectedColumnsState();
    this.state.page = 1

    this.storeState();
    this.reloadPage();
  }

  // We could also just rig the promote:{select,deselect}-all events from
  // the checkbox-select-all-controller and ride with it, but since
  // the vast majority of the code here is self-sustainable...
  toggleSelectAllRows(event) {
    const selectAllCheckbox = event.target

    if (selectAllCheckbox.checked) {
      this.state.selectionMode = 'all';
    } else {
      this.state.selectionMode = 'none';
    }
    this.state.selectedItems.clear();

    this.refreshVisibleCheckboxes();
    this.syncCheckboxes();
    this.syncCounter();
    this.dispatchSelectionEvent();
  }

  toggleColumn(event) {
    if (this.ajaxRequest) this.ajaxRequest.abort();

    if (event.target.checked) {
      this.state.selectedColumns.add(event.target.dataset.toggleableIdentifier);
    } else {
      this.state.selectedColumns.delete(event.target.dataset.toggleableIdentifier);
    }

    this.state.page = 1

    this.storeState();
    this.reloadPage();
  }

  storeState() {
    const stateToStore = {
      ...this.state,
      selectedItems: Array.from(this.state.selectedItems.entries()),
      selectedColumns: Array.from(this.state.selectedColumns.entries())
    };

    localStorage.setItem(this.containerValue, JSON.stringify(stateToStore));
  }

  restoreState(savedState) {
    // Clear local storage
    localStorage.removeItem(this.containerValue);

    // Set state
    const parsed = JSON.parse(savedState);

    parsed.selectedItems = new Map(parsed.selectedItems || []);
    parsed.selectedColumns = new Map(parsed.selectedColumns || []);
    parsed.filters = new Map(parsed.filters || []);

    this.state = parsed;

    // Set hasNext
    this.state.hasNext = this.hasNextValue;

    // Restore search value
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = this.state.search;
    }

    this.updateSortElement();
    this.restoreSelection(true);
  }

  clearSelection(event) {
    this.state.selectedItems.clear();

    if (this.selectableCommunicationStrategyValue === 'checkbox') {
      this.syncCheckboxes();
    }

    this.syncCounter();
    this.restoreSelection();
    this.dispatchSelectionEvent();
  }

  handleRowDeletion(event) {
    // Reset page to current page - 1 so next scroll reloads current page
    // The deduplication logic will handle skipping already-loaded rows
    if (this.state && this.state.page > 1) {
      this.state.page = this.state.page - 1;
    }

    const rowId = event.detail?.rowId;

    if (!rowId) return;

    // Find and remove the row
    const row = this.element.querySelector(`[data-row-id="${rowId}"]`);
    const tableRow = row ? row.closest('.table-body-row') : null;

    if (tableRow) {
      tableRow.classList.add('fade-out');
      setTimeout(() => tableRow.remove(), 200);
    }

    this.decrementRecordCounter();
  }

  toggleSelection(event) {
    const checkbox = event.target;
    const selectionItem = this.extractSelectionItem(checkbox);

    if (!selectionItem.id) return;

    if (checkbox.checked) {
      this.handleItemSelected(selectionItem);
    } else {
      this.handleItemDeselected(selectionItem);
    }

    this.updateUIAfterSelection();
  }

  extractSelectionItem(checkbox) {
    return {
      id: checkbox.dataset.selectableId,
      tooltip: checkbox.dataset.selectableTooltip,
      dataset: checkbox.dataset
    };
  }

  handleItemSelected({ id, tooltip, dataset }) {
    switch (this.state.selectionMode) {
      case 'none':
        this.transitionToSpecificMode(id, tooltip, dataset);
        break;
      case 'specific':
        this.addToSpecificSelection(id, tooltip, dataset);
        this.attemptTransitionToAllMode();
        break;
      case 'all_except':
        this.removeFromExceptions(id);
        break;
    }
  }

  handleItemDeselected({ id, tooltip, dataset }) {
    switch (this.state.selectionMode) {
      case 'specific':
        this.removeFromSpecificSelection(id);
        break;
      case 'all':
        this.transitionToAllExceptMode(id, tooltip, dataset);
        break;
      case 'all_except':
        this.addToExceptions(id, tooltip, dataset);
        break;
    }
  }

  transitionToSpecificMode(id, tooltip, dataset) {
    this.state.selectionMode = 'specific';
    this.state.selectedItems.set(id, { id, tooltip, dataset });
  }

  addToSpecificSelection(id, tooltip, dataset) {
    this.state.selectedItems.set(id, { id, tooltip, dataset });
  }

  attemptTransitionToAllMode() {
    if (this.shouldTransitionToAllMode()) {
      this.state.selectionMode = 'all';
      this.state.selectedItems.clear();
    }
  }

  shouldTransitionToAllMode() {
    return this.allowSelectAllValue &&
           this.state.selectedItems.size === this.state.totalCount &&
           !this.state.search &&
           this.state.filters.size === 0;
  }

  removeFromExceptions(id) {
    this.state.selectedItems.delete(id);
    if (this.state.selectedItems.size === 0) {
      this.state.selectionMode = 'all';
    }
  }

  removeFromSpecificSelection(id) {
    this.state.selectedItems.delete(id);
    if (this.state.selectedItems.size === 0) {
      this.state.selectionMode = 'none';
    }
  }

  transitionToAllExceptMode(id, tooltip, dataset) {
    this.state.selectionMode = 'all_except';
    this.state.selectedItems.set(id, { id, tooltip, dataset });
  }

  addToExceptions(id, tooltip, dataset) {
    this.state.selectedItems.set(id, { id, tooltip, dataset });
    if (this.state.selectedItems.size >= this.state.totalCount) {
      this.state.selectionMode = 'none';
      this.state.selectedItems.clear();
    }
  }

  updateUIAfterSelection() {
    if (this.selectableCommunicationStrategyValue === 'checkbox') {
      this.syncCheckboxes();
    }

    this.syncCounter();
    this.dispatchSelectionEvent();
  }

  refreshVisibleCheckboxes() {
    this.element.querySelectorAll('[data-selectable-id]').forEach((checkbox) => {
      const id = checkbox.dataset.selectableId;

      checkbox.checked = this.isSelected(id);
    })
  }

  isSelected(id) {
    switch(this.state.selectionMode) {
      case 'all':
        return true;
      case 'none':
        return false;
      case 'all_except':
        return !this.state.selectedItems.has(id);
      case 'specific':
        return this.state.selectedItems.has(id);
    }
  }

  syncCheckboxes() {
    this.element.querySelectorAll('.js-data-table-selected-item')
      .forEach(checkbox => checkbox.remove());

    this.state.selectedItems.forEach((_, id) => {
      const checkbox = document.createElement('input');
      checkbox.type = 'hidden';
      checkbox.name = this.selectableCheckboxNameValue;
      checkbox.value = id;
      checkbox.classList.add('js-data-table-selected-item');

      this.element.appendChild(checkbox);
    });
  }

  syncCounter() {
    let count;

    if (this.state.selectionMode == 'all') {
      count = this.recordCountValue;
    } else if (this.state.selectionMode == 'all_except') {
      count = this.recordCountValue - this.state.selectedItems.size;
    } else {
      count = this.state.selectedItems.size;
    }

    const content = count == 1 ?
      this.selectableCounterOneValue :
      this.selectableCounterOtherValue;

    this.selectableCounterTarget.innerText = content.replace(/\d+/, count);

    if (this.hasCountersContainerTarget) {
      this.countersContainerTarget.classList.remove('hidden');
    }

    this.syncTooltips();
  }

  syncTooltips() {
    this.selectableCounterTooltipTarget
      .querySelector('[data-components--tooltip-target="text"]')
      .innerText = [...this.state.selectedItems.values()]
      .map((entry) => entry.tooltip)
      .filter((entry) => entry !== undefined)
      .sort()
      .join(', ');
  }

  dispatchSelectionEvent() {
    document.dispatchEvent(
      new CustomEvent('promote:data-table:selection', {
        detail: {
          selectedItems: this.state.selectedItems,
          selectionMode: this.state.selectionMode
        },
      })
    );
  }

  clearSearch() {
    this.searchInputTarget.value = '';
    this.state.search = '';
    this.state.page = 1;

    this.searchEmptyStateTarget.classList.add('hidden');
    this.tableTarget.classList.remove('hidden');

    this.fetchData();
  }

  handleSearch(event) {
    this.state.search = event.target.value;
    this.state.page = 1;

    this.fetchData();
  }

  fetchData() {
    if (this.ajaxRequest) this.ajaxRequest.abort();

    this.updateCsvDownloadUrl();

    if (this.requestMethodValue.toLowerCase() === 'post') {
      this._fetchDataWithPost();
    } else {
      this._fetchDataWithGet();
    }
  }

  _fetchDataWithGet() {
    Rails.ajax({
      url: this.buildUrl(),
      type: 'GET',
      contentType: 'application/json',
      dataType: 'json',
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      },
      success: this.processData.bind(this),
      // TODO: display error message using an alert?
      error: (response, statusText) => {
        if (statusText !== 'abort') console.error('Error fetching data:', response);
      },
    });
  }

  _fetchDataWithPost() {
    Rails.ajax({
      url: this.urlValue,
      data: this._buildRequestData(),
      type: 'POST',
      contentType: 'application/json',
      dataType: 'json',
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      },
      success: this.processData.bind(this),
      // TODO: display error message using an alert?
      error: (response, statusText) => {
        if (statusText !== 'abort') console.error('Error fetching post data:', response);
      },
    });
  }

  reloadPage() {
    let response;

    if (this.requestMethodValue.toLowerCase() === 'post') {
      response = fetch(this.urlValue,
        {
          method: 'POST',
          body: this._buildRequestData(),
          headers: {
            'Accept': 'text/html'
          }
        })
    } else {
      response = fetch(this.buildUrl(),
        {
          method: 'GET',
          headers: {
            'Accept': 'text/html'
          }
        })
    }

    response.then(response => {
      return response.text();
    })
      .then(html => {
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(html, 'text/html');
        const newContainer = newDoc.querySelector(`#${this.containerValue}`);

        if (newContainer) {
          // Find the new table element
          const newTable = newContainer.querySelector('[data-components--admin-data-table-target="table"]');

          if (newTable && this.hasTableTarget) {
            // Only replace the table section, keeping toolbar (and dropdown) intact
            this.tableTarget.outerHTML = newTable.outerHTML;

            // Update pagination state from the server response
            const newHasNextValue = newContainer.dataset['components-AdminDataTableHasNextValue'];
            this.state.hasNext = newHasNextValue === 'true';

            // Re-initialize the observer with the new DOM elements
            this.setupInfiniteScroll();
            this.restoreSelection();
          }
        }
      })
      .catch(error => {
        // no-op
      });
  }

  processData(data) {
    const rowsContainer = this.tableTarget.querySelector('.rows-section');
    const html = data.rows.join('');

    this.state.hasNext = data.has_next || false;

    // Update record count if present
    if (this.showRecordCountValue && data.records_count_text) {
      const useSearchText = this.state.search && data.search_count_text
      const text = useSearchText ? data.search_count_text : data.records_count_text;

      this.updateRecordCounter(text, data.total_count);
      this.state.totalCount = data.total_count;
    }

    if (this.state.search && data.rows.length === 0) {
      this.toggleElementsVisibility(true)
      return;
    } else if (this.filtered() && data.rows.length === 0) {
      this.toggleElementsVisibility(true);
      return;
    } else {
      this.toggleElementsVisibility(false);
    }

    if (this.state.page === 1) {
      const trigger = this.hasInfiniteScrollTriggerTarget ? this.infiniteScrollTriggerTarget : null;
      rowsContainer.scrollTop = 0;
      rowsContainer.innerHTML = html;

      if (this.scrollInsideValue && trigger != null) {
        rowsContainer.appendChild(trigger);
      }
    } else {
      // Deduplicate: only append rows that aren't already on the page
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const newRows = Array.from(tempDiv.querySelectorAll('.table-body-row'));

      const rowsToAppend = newRows.filter(newRow => {
        // Check if row already exists by comparing data-row-id identifier
        const rowIdElement = newRow.querySelector('[data-row-id]');
        if (rowIdElement) {
          const rowId = rowIdElement.dataset.rowId;
          const existingRow = rowsContainer.querySelector(`[data-row-id="${rowId}"]`);
          return !existingRow;
        }
        return true; // If no identifier, append anyway
      });

      if (this.scrollInsideValue) {
        rowsToAppend.forEach((row) => {
          rowsContainer.insertBefore(row, this.infiniteScrollTriggerTarget);
        });
      } else {
        rowsToAppend.forEach((row) => {
          rowsContainer.appendChild(row);
        });
      }

      this._updatedSelectedItemsState(false);
      this._updateSelectedColumnsState();
    }

    this.handleScrollTrigger();
    this.updateSortElement();
    this.toggleInfiniteScroll();
    this.restoreSelection(true);
  }

  // DOM Updates
  updateSortElement() {
    const el = this.element
      .querySelector(`[data-sortable-identifier=${this.state.sorting.column}]`);

    if (!el) return;

    const nextDirection = this.state.sorting.direction === 'asc' ? 'desc' : 'asc';
    el.dataset.sortableDirection = nextDirection;
  }

  toggleInfiniteScroll() {
    if (!this.hasInfiniteScrollTriggerTarget) return;

    this.infiniteScrollTriggerTarget
      .classList.toggle('hidden', !this.state.hasNext);
  }

  handleScrollTrigger() {
    if (!this.hasInfiniteScrollTriggerTarget) return;

    this.manageObserver(
      this.nextObserver,
      this.infiniteScrollTriggerTarget,
      this.state.hasNext
    );
  }

  toggleElementsVisibility(visible) {
    this.searchEmptyStateTarget.classList.toggle('hidden', !visible);

    this.tableTarget.classList.toggle('hidden', visible);
    this.tableHintTarget.classList.toggle('hidden', visible);

    if (this.hasCountersContainerTarget && !visible) {
      this.countersContainerTarget.classList.remove('hidden');
    }

    if (this.hasToggleableDropdownTarget) {
      this.toggleableDropdownTarget
        .querySelector('.admin-dropdown-container')
        .classList.toggle('disabled', visible);
    }
  }

  restoreSelection(ignoreSync = false) {
    this.element.querySelectorAll('[data-selectable-id]')
      .forEach(checkbox => {
        const id = checkbox.dataset.selectableId;

        checkbox.checked = this.isSelected(id);
     });

    document.dispatchEvent(
      new CustomEvent('promote:data-table:selection', {
        detail: {
          selectedItems: this.state.selectedItems,
          selectionMode: this.state.selectionMode,
          ignoreSync
        },
      })
    );
  }

  manageObserver(observer, target, hasPage) {
    if (hasPage) {
      observer.disconnect();
      observer.observe(target);
    } else {
      target.classList.add('hidden');
    }
  }

  setupInfiniteScroll() {
    if (this.state.hasNext && this.hasInfiniteScrollTriggerTarget) {
      if (this.nextObserver) {
        this.nextObserver.disconnect();
      }
      this.nextObserver = this.initializeObserver(this.handleNextPageLoad);
      this.nextObserver.observe(this.infiniteScrollTriggerTarget);
      this.toggleInfiniteScroll();
    }
  }

  // Helpers
  buildUrl() {
    const url = new URL(this.urlValue);

    Object.entries(this.params()).forEach(([key, value]) => {
      if (value) url.searchParams.append(key, value);
    });

    return url.toString();
  }

  _buildRequestData() {
    const formData = new FormData();

    Object.entries(this.params()).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });

    const csrfParam = document.querySelector("meta[name=\"csrf-param\"]");
    const csrfValue = document.querySelector("meta[name=\"csrf-token\"]");
    if (csrfParam && csrfValue) {
      formData.append(csrfParam.content, csrfValue.content);
    }

    return formData;
  }

  params() {
    const {
      search,
      sorting,
      page,
      filters,
      selectedColumns,
      selectedItems,
      startDate,
      endDate,
      selectionMode
    } = this.state;

    const params = {};

    if (search) params.search = search;
    if (sorting.column) params.sort_by = this.formatSortBy(sorting);
    if (page) params.page = page;
    if (selectedColumns) params.columns = Array.from(selectedColumns).join(' ');
    if (selectedItems) params.selected_items = Array.from(selectedItems.keys()).join(' ');
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    if (selectionMode) params.selection_mode = selectionMode

    if (filters.size) {
      filters.forEach((value, key) => {
        params[key] = value;
      })
    }

    return params;
  }

  updateCsvDownloadUrl() {
    if (!this.hasCsvDownloadLinkTarget || !this.csvUrlValue) return;

    const url = new URL(this.csvUrlValue, window.location.origin);
    const p = this.params();

    if (p.search) url.searchParams.set('search', p.search);
    else url.searchParams.delete('search');

    if (p.sort_by) url.searchParams.set('sort_by', p.sort_by);
    else url.searchParams.delete('sort_by');

    this.state.filters.forEach((value, key) => url.searchParams.set(key, value));

    this.csvDownloadLinkTarget.href = url.href;
  }

  formatSortBy({ column, direction }) {
    return `${column} ${direction}`;
  }

  setHeaderContainerProperties() {
    let element = document.querySelector('.rows-section')
    let header = document.querySelector('.header-section');

    if (!header) return;

    // Calculate the scroll width and set padding-right to accommodate the scrollbar
    let scrollWidth = element.offsetWidth - element.scrollWidth;

    header.style.setProperty('padding-right', `${scrollWidth}px`);
  }

  updateRecordCounter(text, count) {
    if (!this.hasRecordCounterTarget) return;

    const cnt = Number(count || 0);
    if (cnt > 0 || this.state.selectedItems.size > 0) {
      this.recordCounterTarget.textContent = text;
      this.recordCounterTarget.classList.remove('hidden');

      if (this.hasCountersContainerTarget) {
        this.countersContainerTarget.classList.remove('hidden');
      }
    } else {
      this.recordCounterTarget.classList.add('hidden');
    }
  }

  decrementRecordCounter() {
    if (!this.hasRecordCounterTarget) return;

    const currentText = this.recordCounterTarget.textContent;
    const match = currentText.match(/(\d+)/);

    if (match) {
      const currentCount = parseInt(match[1], 10);
      const newCount = Math.max(0, currentCount - 1);

      const isSearchMode = this.state && this.state.search && this.state.search.length > 0;

      let newText;

      if (newCount === 1) {
        if (isSearchMode && this.hasSearchCounterOneValue) {
          newText = this.searchCounterOneValue;
        } else if (this.hasRecordCounterOneValue) {
          newText = this.recordCounterOneValue;
        }
      } else {
        if (isSearchMode && this.hasSearchCounterOtherValue) {
          newText = this.searchCounterOtherValue.replace('count_placeholder', newCount);
        } else if (this.hasRecordCounterOtherValue) {
          newText = this.recordCounterOtherValue.replace('count_placeholder', newCount);
        }
      }

      if (!newText) { newText = currentText.replace(/\d+/, newCount); }

      this.updateRecordCounter(newText, newCount);
    }
  }

  _updatedSelectedItemsState(setSelectedItems = true) {
    if (!this.selectableSelectedElementsValue) return;

    if (setSelectedItems) {
      this.selectableSelectedElementsValue.forEach((element) => {
        this.state.selectedItems.set(element.id.toString(), element);
      });
    }

    const ignoreSync = true;
    this.restoreSelection(ignoreSync);

    if (this.selectableCommunicationStrategyValue === 'checkbox') {
      this.syncCheckboxes();
      this.syncCounter();
      this.syncTooltips();
    }
  }

  _updateSelectedColumnsState() {
    if (!this.hasToggleableCheckboxTarget) return;

    this.state.selectedColumns = new Set();

    this.toggleableCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        this.state.selectedColumns.add(checkbox.dataset.toggleableIdentifier);
      }
    });
  }
}
